import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, X, Sparkles, Send, Copy, RefreshCw, Ban, CheckCircle2, Clock, Link2, Share2 } from "lucide-react";
import { useNativeShare } from "@/hooks/useNativeShare";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fmtDT = (iso) => { try { return new Date(iso).toLocaleString("es-MX", { dateStyle: "medium", timeStyle: "short" }); } catch { return iso || "—"; } };
const money = (n, cur) => `$${Number(n || 0).toLocaleString("es-MX", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ${cur}`;
const hours = (h) => (h < 48 ? `${h} horas` : `${h / 24} días`);

const STATUS = {
  sent: { label: "Enviadas · esperando elección", cls: "text-fuchsia-200 bg-fuchsia-500/15 border-fuchsia-400/40" },
  chosen: { label: "El cliente ya eligió", cls: "text-emerald-200 bg-emerald-500/15 border-emerald-400/40" },
  expired: { label: "Vencidas sin elección", cls: "text-neutral-300 bg-white/5 border-white/15" },
  cancelled: { label: "Canceladas", cls: "text-rose-200 bg-rose-500/15 border-rose-400/40" },
};

/** OpportunitiesModal — admin view of the 3 automatic liquidation offers. */
export const OpportunitiesModal = ({ plan, clientUser, authHeader, onClose }) => {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState("");
  const { canShare, share } = useNativeShare();

  const load = async () => {
    try {
      const r = await axios.get(`${API}/admin/payment-plans/${plan.id}/opportunities`, { headers: authHeader() });
      setData(r.data);
    } catch (e) { toast.error(e?.response?.data?.detail || "Error"); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [plan?.id]);

  const act = async (kind) => {
    setBusy(kind);
    try {
      if (kind === "generate") {
        await axios.post(`${API}/admin/payment-plans/${plan.id}/opportunities`, {}, { headers: authHeader() });
        toast.success("Oportunidades generadas y enviadas al cliente (chat + WhatsApp)");
      } else if (kind === "resend") {
        await axios.post(`${API}/admin/payment-plans/${plan.id}/opportunities/resend`, {}, { headers: authHeader() });
        toast.success("Oportunidades reenviadas");
      } else if (kind === "cancel") {
        await axios.delete(`${API}/admin/payment-plans/${plan.id}/opportunities`, { headers: authHeader() });
        toast.success("Oportunidades canceladas");
      }
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "No se pudo completar"); }
    finally { setBusy(""); }
  };

  if (!plan) return null;
  const opp = data?.opportunities;
  const cur = data?.currency || plan.currency || "MXN";
  const st = opp ? STATUS[opp.status] || STATUS.sent : null;
  const link = opp?.link || "";
  const waText = opp ? `${opp.chat_message || ""}`.trim() : "";
  const phoneDigits = String(clientUser?.phone || "").replace(/\D/g, "");
  const chosen = opp?.options?.find((o) => o.id === opp.chosen_option_id);

  const copy = async (t) => { try { await navigator.clipboard.writeText(t); toast.success("Copiado"); } catch { toast.error("No se pudo copiar"); } };

  return (
    <div className="fixed inset-0 z-[160] bg-black/85 backdrop-blur-md flex items-center justify-center px-3 py-6 overflow-y-auto" data-testid="opportunities-modal" onClick={onClose}>
      <div className="relative w-full max-w-lg bg-gradient-to-br from-[#0a0510] to-black border-2 border-fuchsia-400/50 rounded-2xl p-5 my-auto shadow-[0_0_60px_rgba(217,70,239,0.25)]" onClick={(e) => e.stopPropagation()}>
        <button data-testid="opportunities-close" onClick={onClose} className="absolute top-3 right-3 p-2 rounded-full hover:bg-white/10 text-neutral-400 hover:text-white"><X className="w-4 h-4" /></button>
        <div className="flex items-center gap-2 mb-1">
          <Sparkles className="w-5 h-5 text-fuchsia-300" />
          <h3 className="font-serif text-lg text-white">Oportunidades de liquidación</h3>
        </div>
        <p className="text-xs text-neutral-400 mb-3">
          <b className="text-white">{clientUser?.name || plan.user_name}</b> · {plan.service_name} · saldo <b className="text-amber-300">{money(data?.remaining ?? 0, cur)}</b>
        </p>

        {!data ? (
          <div className="py-8 text-center"><Loader2 className="w-6 h-6 animate-spin text-fuchsia-300 mx-auto" /></div>
        ) : !opp ? (
          <div className="space-y-3">
            <div className="rounded-lg border border-white/10 bg-black/40 p-3 text-xs text-neutral-300">
              Aún no se han enviado oportunidades para este trabajo. El sistema las envía automáticamente al <b className="text-white">primer abono</b>; también puedes generarlas ahora (3 opciones, tope 50 %).
              {data.has_active_promo && <div className="mt-2 text-amber-300">⚠️ Hay una promoción activa — cancélala antes de enviar oportunidades.</div>}
            </div>
            <button data-testid="opportunities-generate" disabled={!!busy || !data.eligible || data.has_active_promo} onClick={() => act("generate")} className="w-full py-2.5 rounded-full flex items-center justify-center gap-1.5 uppercase text-[10px] tracking-widest font-bold bg-fuchsia-500 hover:bg-fuchsia-400 text-black disabled:opacity-50">
              {busy === "generate" ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />} Generar y enviar 3 oportunidades
            </button>
          </div>
        ) : (
          <div className="space-y-3">
            <div className={`text-[10px] uppercase tracking-widest px-2.5 py-1 rounded-full border inline-flex items-center gap-1 ${st.cls}`} data-testid="opportunities-status">
              {opp.status === "chosen" ? <CheckCircle2 className="w-3 h-3" /> : <Clock className="w-3 h-3" />} {st.label}
            </div>
            <div className="text-[10px] text-neutral-500">
              Enviadas {fmtDT(opp.created_at)} {opp.auto ? "(automático al primer abono)" : `por ${opp.sent_by}`} · vigentes hasta {fmtDT(opp.expires_at)}
              {opp.resend_count > 0 && ` · reenviadas ${opp.resend_count}×`}
            </div>

            <div className="grid gap-2">
              {opp.options.map((o) => {
                const isChosen = chosen?.id === o.id;
                return (
                  <div key={o.id} data-testid={`opportunity-option-${o.key}`} className={`rounded-lg border p-2.5 text-xs ${isChosen ? "border-emerald-400/60 bg-emerald-500/10" : "border-white/10 bg-black/40"}`}>
                    <div className="flex items-center justify-between gap-2">
                      <span className="text-white font-semibold">{o.emoji} {o.label} · <span className="text-fuchsia-300">{o.pct}%</span></span>
                      <span className="text-[10px] text-neutral-400">liquida en {hours(o.valid_hours)}</span>
                    </div>
                    <div className="text-neutral-400 mt-0.5">Paga <b className="text-emerald-300">{money(o.new_remaining, cur)}</b> · ahorra {money(o.discount_amount, cur)}</div>
                    {isChosen && <div className="text-[10px] text-emerald-300 mt-1">✓ Elegida {fmtDT(opp.chosen_at)} — promoción activada</div>}
                  </div>
                );
              })}
            </div>

            {opp.status === "sent" && (
              <>
                <div className="rounded-lg border border-white/10 bg-black/40 p-2.5 text-xs">
                  <div className="text-[10px] uppercase tracking-widest text-neutral-400 mb-1 flex items-center gap-1"><Link2 className="w-3 h-3" /> Enlace del cliente</div>
                  <div className="flex items-center gap-2">
                    <code data-testid="opportunities-link" className="flex-1 truncate text-fuchsia-200 text-[11px]">{link}</code>
                    <button data-testid="opportunities-copy-link" onClick={() => copy(link)} className="p-1.5 rounded bg-white/5 hover:bg-white/10 text-neutral-300"><Copy className="w-3.5 h-3.5" /></button>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  {phoneDigits ? (
                    <a data-testid="opportunities-wa" href={`https://wa.me/${phoneDigits}?text=${encodeURIComponent(waText)}`} target="_blank" rel="noreferrer" className="py-2 rounded-full flex items-center justify-center gap-1.5 text-[10px] uppercase tracking-widest font-bold bg-emerald-500 hover:bg-emerald-400 text-black">
                      <Send className="w-3.5 h-3.5" /> WhatsApp
                    </a>
                  ) : (
                    <button onClick={() => copy(waText)} className="py-2 rounded-full flex items-center justify-center gap-1.5 text-[10px] uppercase tracking-widest font-bold bg-emerald-500/60 text-black"><Copy className="w-3.5 h-3.5" /> Copiar mensaje</button>
                  )}
                  {canShare ? (
                    <button data-testid="opportunities-share" onClick={() => share({ text: waText, url: link, title: "Oportunidades de descuento" })} className="py-2 rounded-full flex items-center justify-center gap-1.5 text-[10px] uppercase tracking-widest font-bold bg-[#d4af37] hover:bg-[#f3d878] text-black"><Share2 className="w-3.5 h-3.5" /> Compartir</button>
                  ) : (
                    <button data-testid="opportunities-resend" disabled={!!busy} onClick={() => act("resend")} className="py-2 rounded-full flex items-center justify-center gap-1.5 text-[10px] uppercase tracking-widest font-bold border border-fuchsia-400/60 text-fuchsia-200 hover:bg-fuchsia-500/15 disabled:opacity-50">
                      {busy === "resend" ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />} Reenviar al chat
                    </button>
                  )}
                </div>
                <div className="flex gap-2 pt-1">
                  {canShare && (
                    <button data-testid="opportunities-resend" disabled={!!busy} onClick={() => act("resend")} className="flex-1 py-1.5 rounded-full text-[10px] uppercase tracking-widest border border-fuchsia-400/40 text-fuchsia-200 hover:bg-fuchsia-500/10 disabled:opacity-50">Reenviar al chat</button>
                  )}
                  <button data-testid="opportunities-cancel" disabled={!!busy} onClick={() => act("cancel")} className="flex-1 py-1.5 rounded-full text-[10px] uppercase tracking-widest border border-rose-400/40 text-rose-200 hover:bg-rose-500/10 flex items-center justify-center gap-1 disabled:opacity-50"><Ban className="w-3 h-3" /> Cancelar</button>
                </div>
              </>
            )}

            {(opp.status === "expired" || opp.status === "cancelled") && (
              <button data-testid="opportunities-generate" disabled={!!busy || data.has_active_promo || !data.eligible} onClick={() => act("generate")} className="w-full py-2.5 rounded-full flex items-center justify-center gap-1.5 uppercase text-[10px] tracking-widest font-bold bg-fuchsia-500 hover:bg-fuchsia-400 text-black disabled:opacity-50">
                {busy === "generate" ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />} Generar nuevas oportunidades
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default OpportunitiesModal;
